import { Head } from 'vite-react-ssg';
import bvp800Latin from '@nexa/ui/fonts/bevietnampro-800-latin.woff2?url';
import interLatin from '@nexa/ui/fonts/inter-400_500_600_700-latin.woff2?url';
import { HTML_LANG, OG_LOCALE, X_DEFAULT_LOCALE, alternatesFor, pathFor } from '@nexa/content';
import type { PageId } from '@nexa/content';
import { buildHeadTags } from '@nexa/seo';
import { useLocale } from '@/core/i18n';
import { SITE } from '@/core/config/site.ts';

export interface PageHeadProps {
  readonly pageId: PageId;
  readonly title: string;
  readonly description: string;
  /** Ảnh OG riêng của trang (đường dẫn tương đối). Bỏ trống ⇒ dùng ảnh mặc định của site. */
  readonly image?: string;
  readonly imageAlt?: string;
  readonly noindex?: boolean;
}

/**
 * MỘT component lo toàn bộ head của MỘT trang. Mọi trang bắt buộc render nó.
 *
 * Đặt tập trung như vậy để không trang nào thiếu canonical hay hreflang — thiếu là loại lỗi
 * không ai thấy khi xem trang, chỉ thấy sau vài tuần khi thứ hạng tụt.
 */
export function PageHead({ pageId, title, description, image, imageAlt, noindex }: PageHeadProps) {
  const locale = useLocale();

  const tags = buildHeadTags(
    {
      title,
      description,
      path: pathFor(pageId, locale),
      locale,
      ogLocale: OG_LOCALE[locale],
      alternates: alternatesFor(pageId),
      xDefaultLocale: X_DEFAULT_LOCALE,
      ...(image === undefined ? {} : { image }),
      ...(imageAlt === undefined ? {} : { imageAlt }),
      ...(noindex === undefined ? {} : { noindex }),
    },
    SITE,
  );

  return (
    <Head>
      <html lang={HTML_LANG[locale]} />
      {/*
        Preload ĐÚNG hai file: Be Vietnam Pro 800 (h1 — ứng viên LCP) và Inter (body, variable
        400-700). Preload thêm nữa là tự cạnh tranh băng thông với chính LCP; 9 file còn lại để
        `font-display: swap` tự lo, trình duyệt chỉ tải subset nào thật sự cần.
        `crossOrigin` là BẮT BUỘC với font — thiếu nó thì trình duyệt tải file hai lần.
      */}
      <link rel="preload" as="font" type="font/woff2" href={bvp800Latin} crossOrigin="anonymous" />
      <link rel="preload" as="font" type="font/woff2" href={interLatin} crossOrigin="anonymous" />
      <title>{tags.title}</title>
      {tags.meta.map((tag) =>
        tag.property === undefined ? (
          <meta key={`n:${tag.name ?? ''}:${tag.content}`} name={tag.name} content={tag.content} />
        ) : (
          <meta
            key={`p:${tag.property}:${tag.content}`}
            property={tag.property}
            content={tag.content}
          />
        ),
      )}
      {tags.links.map((link) => (
        <link
          key={`${link.rel}:${link.hrefLang ?? ''}:${link.href}`}
          rel={link.rel}
          href={link.href}
          {...(link.hrefLang === undefined ? {} : { hrefLang: link.hrefLang })}
        />
      ))}
    </Head>
  );
}
